import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "../../utils/axiosClient";

const queryConfig = {
  enabled: true,
  refetchInterval: 1800000,
  staleTime: Infinity,
  placeholderData: (prev: any) => prev,
};

export const useGetProjectDetails = (projectId: string) => {
  return useQuery({
    queryKey: ["projectDetails", projectId],
    queryFn: async () => {
      const { data } = await client.get(`/project?projectId=${projectId}`);
      return data;
    },
    enabled: !!projectId,
    refetchInterval: 1800000,
    staleTime: Infinity,
    placeholderData: (prev: any) => prev,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (projectData: any) => {
      const { data } = await client.post("/project/create", projectData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectList"] });
    },
  });
};

export const useUpdateProjectDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await client.put("project/update-project", payload);
      return data;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
    },
  });
};

export const useSetupPhases = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, phases }: { projectId: string; phases: any[] }) => {
      const { data } = await client.put(`/project/setup-phases`, { projectId, phases });
      return data;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
    },
  });
};

export const useGetProjectUsers = (projectId: string) => {
  return useQuery({
    queryKey: ["projectUsers", projectId],
    queryFn: async () => {
      const { data } = await client.get(`/project/project-users?projectId=${projectId}`);
      return data;
    },
    ...queryConfig,
  });
};

export const useGetOrganizationUsers = () => {
  return useQuery({
    queryKey: ["organizationUsers"],
    queryFn: async () => {
      const { data } = await client.get(`/tenant/members`);
      return data;
    },
    ...queryConfig,
  });
};

export const useEndPhase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId, phaseId, isCompleted, isOngoing, isActive, endDate,
    }: {
      projectId: string; phaseId?: string; isCompleted?: boolean;
      isOngoing?: boolean; isActive?: boolean; endDate?: string;
    }) => {
      const { data } = await client.put(`/project/project-phases`, {
        projectId, phaseId, isCompleted, isOngoing, isActive, endDate,
      });
      return data;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
    },
  });
};

export const useGetProjectList = (userId: number) => {
  return useQuery({
    queryKey: ["projectList", userId],
    queryFn: async () => {
      const { data } = await client.get(`/project/project-list?id=${userId}`);
      return data;
    },
    ...queryConfig,
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId }: { projectId: string }) => {
      const { data } = await client.delete(`/project/delete-project?projectId=${projectId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectList"] });
    },
  });
};

export const useGetProjectPermission = ({
  projectId, userId,
}: {
  projectId: string; userId: number;
}) => {
  return useQuery({
    queryKey: ["projectPermission", projectId, userId],
    queryFn: async () => {
      const { data } = await client.get(
        `/project/project-permissions?projectId=${projectId}&userId=${userId}`
      );
      return data;
    },
    ...queryConfig,
  });
};

export const useMarkSpoc = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, userId, spoc }: { projectId: string; userId: number; spoc: number }) => {
      const { data } = await client.put(`/project/mark-spoc`, { projectId, userId, spoc });
      return data;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
    },
  });
};